import { useCallback, useEffect, useMemo, useState } from "react";
import { Eye, Search, Download, RotateCcw, Trash2, X, Archive } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { cn, initialsOf, dateLocale } from "@/lib/utils";
import { useLang, translateFr as tfr } from "@/i18n";
import ReperesDetail from "@/components/reperes/ReperesDetail";
import WorkspaceBreadcrumb from "@/components/prospecting/WorkspaceBreadcrumb";
import { DiscardDialog } from "@/components/reperes/ReperesDialogs";

export const fmtDate = (iso) => {
  if (!iso) return "—";
  const s = String(iso);
  const d = new Date(s.length === 10 ? s + "T12:00:00" : s);
  return isNaN(d) ? "—" : d.toLocaleDateString(dateLocale(), { day: "numeric", month: "short", year: "numeric" });
};

export const relDays = (iso, tf) => {
  if (!iso) return "—";
  const d = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 864e5));
  return d === 0 ? tf("aujourd'hui") : `il y a ${d} j`;
};

function ContractCell({ p, tf }) {
  if (!p.contract_end_date) return <span className="text-muted-foreground/60 text-xs">—</span>;
  const urgent = p.contract_days_left !== null && p.contract_days_left <= 180;
  const expired = p.contract_days_left !== null && p.contract_days_left < 0;
  return (
    <div className="min-w-0">
      <div className="text-xs tabular-nums">{fmtDate(p.contract_end_date)}</div>
      <div className={cn("text-[11px] font-medium truncate", expired ? "text-athlynx" : urgent ? "text-warning" : "text-muted-foreground")}>
        {tf(p.contract_remaining_label || "")}
      </div>
    </div>
  );
}

const SOURCE_LABELS = { recommendation: "Recommandation", import: "Import", manual: tfr("Ajout manuel") };

export default function Reperes() {
  const { tf } = useLang();
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [checked, setChecked] = useState(new Set());
  const [q, setQ] = useState("");
  const [fSport, setFSport] = useState("all");
  const [fContract, setFContract] = useState("all");
  const [fAge, setFAge] = useState("all");
  const [fSource, setFSource] = useState("all");
  const [sort, setSort] = useState("recent");
  const [showDiscarded, setShowDiscarded] = useState(false);
  const [discardDlg, setDiscardDlg] = useState(null); // {ids:[...], name}
  const [mobileOpen, setMobileOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/reperes");
      setData(r.data);
    } catch { toast.error(tf("Erreur de chargement")); }
  }, [tf]);
  useEffect(() => { load(); }, [load]);

  const prospects = useMemo(() => data?.prospects || [], [data]);
  const discarded = useMemo(() => data?.discarded || [], [data]);
  const counts = data?.counts || {};
  const sports = useMemo(() => [...new Set(prospects.map((p) => p.sport))], [prospects]);

  const list = useMemo(() => {
    let arr = [...prospects];
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      arr = arr.filter((p) => `${p.name} ${p.club} ${p.position}`.toLowerCase().includes(s));
    }
    if (fSport !== "all") arr = arr.filter((p) => p.sport === fSport);
    const dl = (p) => p.contract_days_left;
    if (fContract === "6m") arr = arr.filter((p) => dl(p) !== null && dl(p) >= 0 && dl(p) <= 183);
    else if (fContract === "12m") arr = arr.filter((p) => dl(p) !== null && dl(p) >= 0 && dl(p) <= 366);
    else if (fContract === "expired") arr = arr.filter((p) => dl(p) !== null && dl(p) < 0);
    else if (fContract === "none") arr = arr.filter((p) => !p.contract_end_date);
    if (fAge === "minor") arr = arr.filter((p) => p.minor);
    else if (fAge === "adult") arr = arr.filter((p) => !p.minor);
    if (fSource !== "all") arr = arr.filter((p) => (p.source_type || "none") === fSource);
    const cend = (p) => (p.contract_days_left ?? 99999);
    if (sort === "recent") arr.sort((a, b) => (b.discovered_at || "").localeCompare(a.discovered_at || ""));
    else if (sort === "oldest") arr.sort((a, b) => (a.discovered_at || "").localeCompare(b.discovered_at || ""));
    else if (sort === "contract") arr.sort((a, b) => cend(a) - cend(b));
    else if (sort === "age") arr.sort((a, b) => (a.age ?? 99) - (b.age ?? 99));
    else if (sort === "name") arr.sort((a, b) => (a.name || "").localeCompare(b.name || "", "fr"));
    return arr;
  }, [prospects, q, fSport, fContract, fAge, fSource, sort]);

  const current = useMemo(
    () => prospects.find((p) => p.id === selected) || list[0] || null,
    [prospects, selected, list]
  );

  const resetFilters = () => { setQ(""); setFSport("all"); setFContract("all"); setFAge("all"); setFSource("all"); setSort("recent"); };
  const toggleCheck = (id) => setChecked((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const afterAction = () => { setDiscardDlg(null); setChecked(new Set()); load(); };

  const exportCsv = (rows) => {
    const head = ["Athlète", "Club", "Poste", "Niveau", "Âge", "Fin de contrat", "Repéré le", "Source"].map((s) => tfr(s));
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [head, ...rows.map((p) => [
      p.name, p.club || "Sans club", p.position || "", p.level || "", p.age ?? "",
      p.contract_end_date || "", (p.discovered_at || "").slice(0, 10),
      p.source_label || SOURCE_LABELS[p.source_type] || "",
    ])].map((r) => r.map(esc).join(";"));
    const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "athlynx_reperes.csv";
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success(`${rows.length} ${tf("prospect(s) exporté(s)")}`);
  };

  const restore = async (pid) => {
    try {
      await api.post(`/reperes/${pid}/restore`);
      toast.success(tf("Prospect restauré"));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || tf("Erreur")); }
  };

  return (
    <div className="animate-fade-in p-4 md:p-6 space-y-4 md:space-y-5 max-w-[1600px] mx-auto min-w-0" data-testid="reperes-page">
      {/* Breadcrumb + header */}
      <div>
        <WorkspaceBreadcrumb currentLabel={tfr("Repérés")} testid="reperes-breadcrumb-dashboard" />
        <div className="flex flex-wrap items-start justify-between gap-4 mt-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="h-11 w-11 rounded-xl bg-athlynx/10 border border-athlynx/25 flex items-center justify-center shrink-0">
            <Eye className="h-5 w-5 text-athlynx" />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold tracking-tight">{tf("Repérés")}</h1>
            <p className="text-sm font-medium text-foreground/80">{tf("Qui vaut la peine d'être approché ?")}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{tf("Centralisez vos prospects et préparez le bon premier contact.")}</p>
          </div>
        </div>
        <div className="rounded-2xl border border-border bg-card px-4 py-3 flex items-center gap-4" data-testid="reperes-summary">
          <div>
            <div className="text-3xl font-bold text-foreground tabular-nums leading-none" data-testid="reperes-stock">{counts.total ?? "—"}</div>
            <div className="text-[11px] text-muted-foreground mt-1">{tf("athlètes repérés")}</div>
          </div>
          <div className="text-[11px] text-muted-foreground border-l border-border pl-4">
            +{data?.flow_7d?.entries ?? 0} {tf((data?.flow_7d?.entries ?? 0) > 1 ? "entrées" : "entrée")} · {data?.flow_7d?.exits ?? 0} {tf((data?.flow_7d?.exits ?? 0) > 1 ? "sorties" : "sortie")} {tf("ces 7 jours")}
          </div>
        </div>
        </div>
      </div>

      {/* Search + filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[170px] max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={tf("Rechercher un athlète…")}
            className="h-9 pl-8 text-sm" data-testid="reperes-search" />
        </div>
        {sports.length > 1 && (
          <Select value={fSport} onValueChange={setFSport}>
            <SelectTrigger className="h-9 w-[120px] text-xs" data-testid="reperes-filter-sport"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">{tf("Tous les sports")}</SelectItem>
              {sports.map((s) => <SelectItem key={s} value={s}>{tfr(s)}</SelectItem>)}</SelectContent>
          </Select>
        )}
        <Select value={fContract} onValueChange={setFContract}>
          <SelectTrigger className="h-9 w-[150px] text-xs" data-testid="reperes-filter-contract"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{tf("Fin de contrat : tous")}</SelectItem>
            <SelectItem value="6m">{tf("≤ 6 mois")}</SelectItem>
            <SelectItem value="12m">{tf("≤ 12 mois")}</SelectItem>
            <SelectItem value="expired">{tf("Contrat expiré")}</SelectItem>
            <SelectItem value="none">{tf("Non renseignée")}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={fAge} onValueChange={setFAge}>
          <SelectTrigger className="h-9 w-[110px] text-xs" data-testid="reperes-filter-age"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{tf("Âge : tous")}</SelectItem>
            <SelectItem value="minor">{tf("Mineurs")}</SelectItem>
            <SelectItem value="adult">{tf("Majeurs")}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={fSource} onValueChange={setFSource}>
          <SelectTrigger className="h-9 w-[140px] text-xs" data-testid="reperes-filter-source"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{tf("Source : toutes")}</SelectItem>
            <SelectItem value="recommendation">{tf("Recommandation")}</SelectItem>
            <SelectItem value="import">{tf("Import")}</SelectItem>
            <SelectItem value="manual">{tf("Ajout manuel")}</SelectItem>
            <SelectItem value="none">{tf("Non renseignée")}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="h-9 w-[200px] text-xs" data-testid="reperes-sort"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="recent">{tf("Repéré récemment")}</SelectItem>
            <SelectItem value="oldest">{tf("Repéré depuis le plus longtemps")}</SelectItem>
            <SelectItem value="contract">{tf("Fin de contrat la plus proche")}</SelectItem>
            <SelectItem value="age">{tf("Âge")}</SelectItem>
            <SelectItem value="name">{tf("Nom")}</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="ghost" size="sm" className="h-9 text-xs" onClick={resetFilters} data-testid="reperes-reset-filters">
          {tf("Réinitialiser")}
        </Button>
        <Button variant="outline" size="sm" className="h-9 text-xs" onClick={() => exportCsv(list)} data-testid="reperes-export">
          <Download className="h-3.5 w-3.5 mr-1.5" />{tf("Exporter")}
        </Button>
      </div>

      {/* Bulk bar */}
      {checked.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-secondary/50 px-3 py-2" data-testid="reperes-bulk-bar">
          <span className="text-xs font-semibold">{checked.size} {tf("sélectionné(s)")}</span>
          <Button variant="outline" size="sm" className="h-8 text-xs"
            onClick={() => exportCsv(list.filter((p) => checked.has(p.id)))} data-testid="bulk-export">
            <Download className="h-3.5 w-3.5 mr-1" />{tf("Exporter la sélection")}
          </Button>
          <Button variant="outline" size="sm" className="h-8 text-xs text-athlynx border-athlynx/30"
            onClick={() => setDiscardDlg({ ids: [...checked] })} data-testid="bulk-discard">
            <Trash2 className="h-3.5 w-3.5 mr-1" />{tf("Écarter")}
          </Button>
          <Button variant="ghost" size="sm" className="h-8 text-xs ml-auto" onClick={() => setChecked(new Set())} data-testid="bulk-clear">
            <X className="h-3.5 w-3.5 mr-1" />{tf("Tout désélectionner")}
          </Button>
        </div>
      )}

      {/* List + Detail */}
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,5fr)_minmax(0,6fr)] gap-4 xl:gap-5 items-start min-w-0">
        <div className="min-w-0 space-y-2">
          <div className="rounded-2xl border border-border bg-card min-w-0 overflow-hidden" data-testid="reperes-list">
            {/* Column headers */}
            <div className="hidden sm:flex items-center gap-3 px-3 py-2 border-b border-border/70 text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">
              <span className="w-4" />
              <span className="flex-1 min-w-0">{tf("Athlète")}</span>
              <span className="hidden md:block w-14 shrink-0">{tf("Niveau")}</span>
              <span className="w-28 shrink-0">{tf("Fin de contrat")}</span>
              <span className="hidden lg:block w-24 shrink-0 text-right">{tf("Repéré le")}</span>
            </div>
            {!data ? (
              <div className="p-4 space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-14 rounded-xl bg-secondary/60 animate-pulse" />)}</div>
            ) : list.length === 0 ? (
              <div className="py-14 text-center text-sm text-muted-foreground" data-testid="reperes-empty">
                {tf("Aucun prospect ne correspond à ces critères.")}
              </div>
            ) : (
              <ul className="divide-y divide-border/70">
                {list.map((p) => (
                  <li key={p.id}>
                    <div
                      role="button" tabIndex={0}
                      onClick={() => {
                        setSelected(p.id);
                        if (window.matchMedia("(max-width: 1279px)").matches) setMobileOpen(true);
                      }}
                      onKeyDown={(e) => { if (e.key === "Enter") { setSelected(p.id); if (window.matchMedia("(max-width: 1279px)").matches) setMobileOpen(true); } }}
                      className={cn("w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors cursor-pointer min-w-0",
                        current?.id === p.id ? "bg-secondary/60 border-l-2 border-l-foreground/70" : "hover:bg-secondary/50 border-l-2 border-l-transparent")}
                      data-testid={`reperes-row-${p.id}`}
                    >
                      <input type="checkbox" checked={checked.has(p.id)} onChange={() => toggleCheck(p.id)}
                        onClick={(e) => e.stopPropagation()} className="accent-[#E11D48] shrink-0"
                        aria-label={`${tf("Sélectionner")} ${p.name}`}
                        data-testid={`reperes-check-${p.id}`} />
                      <Avatar className="h-9 w-9 shrink-0">
                        <AvatarImage src={p.photo_url} alt="" />
                        <AvatarFallback className="text-[11px]">{initialsOf(p.name)}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-sm font-semibold truncate">{p.name}</span>
                          {p.minor && <span className="shrink-0 text-[10px] font-semibold uppercase rounded-full border border-warning/40 bg-warning/10 text-warning px-1.5 py-px" data-testid={`minor-badge-${p.id}`}>{tf("Mineur")}</span>}
                        </div>
                        <div className="text-[11px] text-muted-foreground truncate">
                          {[p.club || tf("Sans club"), p.position, p.age ? `${p.age} ans` : null].filter(Boolean).join(" · ")}
                        </div>
                      </div>
                      <div className="hidden md:block w-14 shrink-0 text-xs text-muted-foreground">{p.level || "—"}</div>
                      <div className="hidden sm:block w-28 shrink-0"><ContractCell p={p} tf={tf} /></div>
                      <div className="hidden lg:block w-24 shrink-0 text-right">
                        <div className="text-xs tabular-nums">{fmtDate(p.discovered_at)}</div>
                        <div className="text-[11px] text-muted-foreground">{relDays(p.discovered_at, tf)}</div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Discarded (archived) prospects */}
          {counts.discarded > 0 && (
            <div>
              <button onClick={() => setShowDiscarded((v) => !v)}
                className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors px-1 py-1"
                data-testid="reperes-toggle-discarded">
                <Archive className="h-3.5 w-3.5" />
                {showDiscarded ? tf("Masquer les écartés") : `${tf("Voir les écartés")} (${counts.discarded})`}
              </button>
              {showDiscarded && (
                <div className="mt-1 rounded-2xl border border-border/70 bg-card/50" data-testid="reperes-discarded-list">
                  <ul className="divide-y divide-border/50">
                    {discarded.map((p) => (
                      <li key={p.id} className="flex items-center gap-3 px-3 py-2.5 min-w-0">
                        <Avatar className="h-8 w-8 shrink-0 opacity-60">
                          <AvatarImage src={p.photo_url} alt="" />
                          <AvatarFallback className="text-[10px]">{initialsOf(p.name)}</AvatarFallback>
                        </Avatar>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-muted-foreground truncate">{p.name}</div>
                          <div className="text-[11px] text-muted-foreground/70 truncate">
                            {tf("Écarté")}{p.discarded_at ? ` ${relDays(p.discarded_at, tf)}` : ""}{p.discard_reason ? ` · ${tf(p.discard_reason)}` : ""}
                          </div>
                        </div>
                        <Button variant="outline" size="sm" className="h-8 text-xs shrink-0"
                          onClick={() => restore(p.id)} data-testid={`restore-${p.id}`}>
                          <RotateCcw className="h-3.5 w-3.5 mr-1" />{tf("Restaurer")}
                        </Button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Desktop detail */}
        <div className="hidden xl:block min-w-0">
          {current ? <ReperesDetail key={current.id} p={current} tf={tf} onChanged={load} onDiscard={() => setDiscardDlg({ ids: [current.id], name: current.name })} /> : (
            <div className="rounded-2xl border border-dashed border-border py-16 text-center text-sm text-muted-foreground">
              {tf("Sélectionnez un athlète")}
            </div>
          )}
        </div>
      </div>

      {/* Mobile detail sheet */}
      <div className="xl:hidden">
        <Sheet open={mobileOpen && !!current} onOpenChange={setMobileOpen}>
          <SheetContent side="bottom" className="inset-x-2 min-h-[60dvh] max-h-[calc(100dvh-104px)] overflow-y-auto rounded-t-2xl border p-4 pb-[max(16px,env(safe-area-inset-bottom))]">
            <SheetTitle className="sr-only">{current?.name || tf("Athlète")}</SheetTitle>
            <SheetDescription className="sr-only">{tf("Détail de l'athlète sélectionné")}</SheetDescription>
            {current && <ReperesDetail key={current.id} p={current} tf={tf} onChanged={load} onDiscard={() => setDiscardDlg({ ids: [current.id], name: current.name })} />}
          </SheetContent>
        </Sheet>
      </div>

      {discardDlg && <DiscardDialog ids={discardDlg.ids} name={discardDlg.name} tf={tf} onClose={() => setDiscardDlg(null)} onDone={afterAction} />}
    </div>
  );
}
